// src/pages/CreatorAuthPage.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import { User, Mail, Briefcase, ImageIcon, FileText } from "lucide-react";

const CreatorAuthPage = () => {
  const navigate = useNavigate();
  const { registerCreator, login } = useAuth();
  const [isSignup, setIsSignup] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    category: "",
    bio: "",
    profileImage: ""
  });

  const categories = [
    "Music", "Comedy", "Art", "Film & Video", "Food & Drink",
    "Lifestyle", "Education", "Technology", "Gaming", "Fitness & Wellness",
    "Fashion & Beauty", "Business & Finance", "Other"
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (isSignup) {
      // Register new creator
      registerCreator(formData);
      navigate("/dashboard");
    } else {
      // Login existing creator with strong password check
      if (formData.password === "Cr3at0r_Adn@k1!2026") {
        login("creator");
        navigate("/dashboard");
      } else {
        alert("Invalid email or password");
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">
            {isSignup ? "Join Adnaki as a Creator" : "Creator Login"}
          </h1>
          <p className="text-slate-600">
            {isSignup 
              ? "Start building your community and monetizing your passion" 
              : "Welcome back! Log in to your creator dashboard"}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {isSignup ? (
            <>
              {/* Signup Fields */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <User className="inline w-4 h-4 mr-1" />
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Mail className="inline w-4 h-4 mr-1" />
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Briefcase className="inline w-4 h-4 mr-1" />
                  Category
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select your category</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <FileText className="inline w-4 h-4 mr-1" />
                  Bio
                </label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Tell your fans about yourself..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <ImageIcon className="inline w-4 h-4 mr-1" />
                  Profile Image URL (optional)
                </label>
                <input
                  type="url"
                  name="profileImage"
                  value={formData.profileImage}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="https://example.com/your-image.jpg"
                />
              </div>
            </>
          ) : (
            <>
              {/* Login Fields */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Mail className="inline w-4 h-4 mr-1" />
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter your password"
                />
              </div>
            </>
          )}

          {/* Submit Button */}
          <Button type="submit" className="w-full" size="lg">
            {isSignup ? "Create Creator Account" : "Log In to Dashboard"}
          </Button>

          {/* Toggle between Login/Signup */}
          <div className="text-center pt-4 border-t">
            <button
              type="button"
              onClick={() => setIsSignup(!isSignup)}
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              {isSignup 
                ? "Already have an account? Log in" 
                : "New to Adnaki? Create an account"}
            </button>
          </div>

          {/* Back to Main Login */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="text-slate-600 hover:text-slate-700 text-sm"
            >
              ← Back to main login
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default CreatorAuthPage;
